import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * useReviewerDecisionMutation
 *
 * Single-cell write path for compliance verdicts. Wire shape carries a
 * `decisions: [...]` array because the backend already supports batch
 * (Phase 4 "Approve all" toolbar lands as a pure FE change), but today
 * every Save invokes this hook with exactly one decision.
 *
 * Behavior:
 *   - Optimistic: we splice the new compliance object into the
 *     `useReviewerCampaign` cache *before* the network call so the chip
 *     flips state immediately.
 *   - Rollback: on error or per-cell `ok: false`, the previous cache
 *     snapshot is restored and the caller's `onError` fires with a
 *     machine-readable code so the UI can render targeted copy
 *     (note_required, content_changed, content_missing, ...).
 *   - Reconciliation: on success we replace the optimistic blob with
 *     the server's authoritative `compliance` (carries the real
 *     `updated_at`, `reviewer_user_id`, `is_stale=false`, etc.) so the
 *     cache stays bit-identical to a fresh GET.
 *
 * Consumers pass `expectedContentHash` straight through from the GET
 * response. The server uses it as a TOCTOU guard — if the advisor
 * mutated the cell mid-review, we get back `error_code: 'content_changed'`
 * and the cache is rolled back without a stale write landing.
 */
export function useReviewerDecisionMutation(campaignId) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = user?.id && campaignId
    ? ['reviewerCampaign', user.id, campaignId]
    : null;

  return useMutation({
    mutationFn: async ({
      contactMembershipId,
      configId,
      status,
      note,
      expectedContentHash,
    }) => {
      const { data } = await api.post(
        `/api/reviews/campaigns/${campaignId}/decisions`,
        {
          decisions: [
            {
              contact_membership_id: contactMembershipId,
              config_id: configId,
              status,
              note: note ?? null,
              expected_content_hash: expectedContentHash ?? null,
              // Per-cell write from the popover — explicitly NOT a
              // bulk action. Sticks (preserved against bulk runs) so
              // reviewer-authored evidence with a thoughtful note isn't
              // overwritten by a hasty bulk-mark-compliant.
              bulk_applied: false,
            },
          ],
        },
      );
      const result = data?.results?.[0];
      if (!result) {
        throw new Error('Server returned no decision result.');
      }
      if (!result.ok) {
        const err = new Error(result.error_message || 'Could not save decision.');
        err.code = result.error_code || 'unknown';
        throw err;
      }
      return result;
    },

    onMutate: async (vars) => {
      if (!queryKey) return { prev: null };
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData(queryKey);

      if (!prev) return { prev: null };

      const optimisticCompliance = {
        status: vars.status,
        note: vars.note ?? null,
        decided_by: 'human',
        // Per-cell popover writes are NOT bulk-applied; setting it
        // explicitly keeps the optimistic cache shape identical to the
        // server's authoritative response after reconciliation.
        bulk_applied: false,
        reviewer_user_id: user?.id || null,
        updated_at: new Date().toISOString(),
        is_stale: false,
      };

      const nextContacts = (prev.contacts || []).map((row) => {
        if (row.contact_membership_id !== vars.contactMembershipId) return row;
        const nextDrafts = { ...(row.drafts || {}) };
        const cell = nextDrafts[vars.configId] || {};
        nextDrafts[vars.configId] = {
          ...cell,
          compliance: optimisticCompliance,
        };
        return { ...row, drafts: nextDrafts };
      });

      queryClient.setQueryData(queryKey, { ...prev, contacts: nextContacts });
      return { prev };
    },

    onError: (_err, _vars, context) => {
      if (queryKey && context?.prev) {
        queryClient.setQueryData(queryKey, context.prev);
      }
    },

    onSuccess: (result, vars) => {
      if (!queryKey) return;
      const current = queryClient.getQueryData(queryKey);
      if (!current) return;

      const nextContacts = (current.contacts || []).map((row) => {
        if (row.contact_membership_id !== vars.contactMembershipId) return row;
        const nextDrafts = { ...(row.drafts || {}) };
        const cell = nextDrafts[vars.configId] || {};
        nextDrafts[vars.configId] = {
          ...cell,
          compliance: result.compliance,
        };
        return { ...row, drafts: nextDrafts };
      });

      queryClient.setQueryData(queryKey, { ...current, contacts: nextContacts });
    },
  });
}
